import PropTypes from 'prop-types';
import { createContext, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { copyFilesOriginDestinyEnum } from '../constants/constants';
import useToast from '../hooks/useToast';
import { postFilesRequestSDS, getFilesRequestSDS } from '../services/serviceRequestAttachment';
import {
  copyFilesFromOneRequestToAnother,
  uploadFileTemplateRequest,
  getFilesIdsFromTemplateRequest
} from '../services/templateServiceRequest';

import LoaderContext from './LoaderProvider';

const FilesContext = createContext();

export const useFilesContext = () => useContext(FilesContext);

const FilesProvider = ({ children }) => {
  const [dataAdjuntos, setDataAdjuntos] = useState([]);
  const [attachmentsByAccordion, setAttachmentsByAccordion] = useState({});
  const [select, setSelect] = useState([]);
  const history = useHistory();
  const { setLoader } = useContext(LoaderContext);
  const isCreate =
    history.location.pathname === '/create' || history.location.pathname === '/createMultiple';
  const isEditTemplate = history.location.pathname.includes('/edit-template/request');
  const idPath = history.location.pathname.split('/').slice(-1)[0];
  const { setNewToast } = useToast();

  const getAccordionAttachments = (accordionId) => attachmentsByAccordion[accordionId] || [];

  const postFilesClient = async (id, filesToAdd) => {
    if (!isCreate) {
      setLoader(true);
    }
    const hasServiceRequestId = !!filesToAdd[0]?.serviceRequestId;
    const files = hasServiceRequestId ? filesToAdd?.map((file) => file?.file) : filesToAdd;
    const formData = new FormData();

    files?.map((file) => formData.append('archivosAdjuntos', file));
    try {
      if (isEditTemplate) {
        await uploadFileTemplateRequest(id, formData);
      } else {
        await postFilesRequestSDS({ id, archivosAdjuntos: formData, isMonthly: false });
      }

      // Solo si es edit llamo al servicio GET
      // dado que en caso de eliminarlo se requiere el ID para ello
      if (!isCreate) {
        let res;

        if (isEditTemplate) {
          res = await getFilesIdsFromTemplateRequest(id);
        } else {
          res = await getFilesRequestSDS(id);
        }
        let newAttachmentsData = res.activos;

        if (hasServiceRequestId || isEditTemplate) {
          const newAttachments = res.activos
            .filter(
              (attachment) =>
                filesToAdd.findIndex((file) => file?.nombre === attachment?.nombre) >= 0
            )
            .map((attachment) => ({
              ...attachment,
              accordionId: filesToAdd[0]?.accordionId,
              serviceRequestId: filesToAdd[0]?.serviceRequestId
            }));

          const filteredDataAdjuntos = dataAdjuntos.filter(
            (attachment) => filesToAdd.findIndex((file) => file?.nombre !== attachment?.nombre) >= 0
          );

          newAttachmentsData = [...newAttachments, ...filteredDataAdjuntos];
        }
        // Solo muestro los activos
        setDataAdjuntos(newAttachmentsData);
        setLoader(false);
      }
      setNewToast({
        type: 'SUCCESS',
        message: 'Archivo subido con éxito.'
      });
      setLoader(false);
    } catch (err) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const postFilesClientMultiple = async (accordion, idRequest) => {
    try {
      const formData = new FormData();

      const accordionAttachments = getAccordionAttachments(accordion.accordionId);
      const attachmentsWithFiles = accordionAttachments.filter((attachment) => !!attachment?.file);

      if (attachmentsWithFiles?.length) {
        attachmentsWithFiles.forEach((attachment) => {
          const fileExtension = attachment?.nombre.split('.').slice(-1)[0];
          const fileBaseName = attachment?.nombre.split('.').slice(0, -1).join('.');
          const newFileName = `${fileBaseName}_${idRequest}.${fileExtension}`;

          formData.append('archivosAdjunto', attachment?.file, newFileName);
        });

        await postFilesRequestSDS({ id: idRequest, archivosAdjuntos: formData, isMonthly: false });

        setNewToast({
          type: 'SUCCESS',
          message: 'Archivo subido con éxito.'
        });
      }
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const getNameOfFile = (file) => {
    const fileExtension = file?.nombre.split('.').slice(-1)[0];
    const fileBaseName = file?.nombre.split('.').slice(0, -1).join('.');

    return `${fileBaseName}.${fileExtension}`;
  };

  const postFilesClientServiceTemplate = async (accordion, templateRequestId) => {
    try {
      const formData = new FormData();
      const accordionAttachments = getAccordionAttachments(accordion?.accordionId);

      const attachmentsWithFiles = accordionAttachments.filter((attachment) => !!attachment?.file);

      if (attachmentsWithFiles.length) {
        attachmentsWithFiles.forEach((attachment) => {
          formData.append('archivosAdjunto', attachment?.file, getNameOfFile(attachment));
        });

        await uploadFileTemplateRequest(templateRequestId, formData);
      }
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const copyExistingFiles = async (
    accordion,
    idOrigen,
    idDestino,
    isDraw = false,
    isApplyTemplateRequest = false,
    isReplyRequest = false
  ) => {
    try {
      const accordionAttachments = getAccordionAttachments(accordion?.accordionId);
      const namesOfFilesToCopy = accordionAttachments
        .filter((attachment) => !attachment?.file)
        .map((attachment) => getNameOfFile(attachment));

      let typeOfCopy;

      if (isDraw) typeOfCopy = copyFilesOriginDestinyEnum.BorradorAPlantilla;
      if (isApplyTemplateRequest) typeOfCopy = copyFilesOriginDestinyEnum.PlantillaASolicitud;
      if (isReplyRequest) typeOfCopy = copyFilesOriginDestinyEnum.SolicitudASolicitud;

      await copyFilesFromOneRequestToAnother(idOrigen, idDestino, namesOfFilesToCopy, typeOfCopy);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const setListFiles = (files, accordionId, serviceRequestId, isDraw = false) => {
    const newFiles = files.map((file) => ({
      id: file?.name,
      nombre: file?.name || file?.nombre,
      peso: file?.size || file?.peso,
      fechaSubida: file?.lastModifiedDate?.toISOString() || file?.fechaSubida,
      file,
      accordionId,
      serviceRequestId
    }));

    if (isCreate || !serviceRequestId || isDraw) {
      setDataAdjuntos((state) => [...state, ...newFiles]);
    } else if (!isCreate && serviceRequestId && !isDraw) {
      postFilesClient(serviceRequestId, newFiles);
    }
  };

  // Verificar si es necesario esta función
  const onDrop = (acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.readAsArrayBuffer(file);
    });
  };

  const onDropAccepted = (files, event) => {
    const accordionId = event.target?.getAttribute('accordionid');
    const serviceRequestId = event.target?.getAttribute('servicerequestid');
    const templateRequestId = event.target?.getAttribute('templateRequestId');
    // Esto quiere decir que la plantilla se esta creando a partir de un borrador de SDS
    const isDraw = event.target?.getAttribute('isDraw');
    const accordionAttachments = dataAdjuntos.filter((file) => file?.accordionId === accordionId);
    const values = accordionId ? accordionAttachments : dataAdjuntos;
    // Archivos repetidos
    const repeatedAttachments = files.filter((adj) =>
      values.some(
        (fileAdj) => fileAdj.nombre === adj.name || fileAdj.nombre === `${idPath}_${adj.name}`
      )
    );

    // Archivos NO repetidos
    const noRepeatedAttachments = files.filter(
      (adj) =>
        !values.some(
          (fileAdj) => fileAdj.nombre === adj.name || fileAdj.nombre === `${idPath}_${adj.name}`
        )
    );

    // No se cargan los archivos repetidos
    // se notifica cuáles archivos son
    if (repeatedAttachments.length) {
      const quantitiesAttachments = repeatedAttachments.length > 1;

      setNewToast({
        type: 'ERROR',
        message: `${
          quantitiesAttachments
            ? `Los archivos
          ${repeatedAttachments.map(
            (repAdj, ind) => `${repAdj.name}
          ${ind + 1 === repeatedAttachments.length ? ',' : '-'}`
          )} ya se encuentran cargados`
            : `El archivo ${repeatedAttachments[0].name} ya se encuentra cargado`
        }`
      });
    }

    // Se cargan los archivos no repetidos
    if (noRepeatedAttachments.length) {
      setListFiles(
        noRepeatedAttachments,
        accordionId,
        isEditTemplate ? templateRequestId : serviceRequestId,
        isDraw
      );
    }
  };

  const selectAll = () => {
    if (select.length < dataAdjuntos.length) {
      const ids = dataAdjuntos?.map((item) => item.id);

      setSelect(ids);
    } else {
      setSelect([]);
    }
  };

  const arrangeAttachmentsByAccordionId = () => {
    const newAttachmentsByAccordion = {};

    dataAdjuntos.forEach((attachment) => {
      const attachments = newAttachmentsByAccordion[attachment.accordionId];

      if (attachments && attachments?.length) {
        attachments.push(attachment);
      } else {
        newAttachmentsByAccordion[attachment.accordionId] = [attachment];
      }
    });
    setAttachmentsByAccordion(newAttachmentsByAccordion);
  };

  const setAccordionAttachments = (accordionId, newAccordionAttachments) => {
    const newDataAdjuntos = dataAdjuntos.filter((adj) => {
      if (adj?.accordionId !== accordionId) return true;

      return newAccordionAttachments.some((attachment) => attachment?.id === adj?.id);
    });

    setDataAdjuntos(newDataAdjuntos);
  };

  const deleteAccordionFiles = (accordionId) => {
    const newAttachmentsData = dataAdjuntos.filter(
      (attachmentData) => attachmentData.accordionId !== accordionId
    );

    setDataAdjuntos(newAttachmentsData);
  };

  useEffect(() => {
    if (dataAdjuntos.every((attachment) => attachment?.accordionId))
      arrangeAttachmentsByAccordionId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdjuntos]);

  return (
    <FilesContext.Provider
      value={{
        attachmentsByAccordion,
        copyExistingFiles,
        dataAdjuntos,
        deleteAccordionFiles,
        getAccordionAttachments,
        onDrop,
        onDropAccepted,
        postFilesClient,
        postFilesClientMultiple,
        postFilesClientServiceTemplate,
        select,
        selectAll,
        setAccordionAttachments,
        setDataAdjuntos
      }}
    >
      {children}
    </FilesContext.Provider>
  );
};

FilesProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default FilesProvider;
